import { useMemo, useState } from 'react';
import { Loader2, PackageSearch, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useRetailStock, useAdjustStock } from '@/hooks/api/useRetailStock';
import { StockAdjustmentReason } from '@pms/shared';
import type { StockRow } from '@/services/api/retailStock.api';

// ─── Constants ─────────────────────────────────────────────────────────────────

const REASON_LABELS: Record<StockAdjustmentReason, string> = {
  [StockAdjustmentReason.RECEIVED]: 'Received',
  [StockAdjustmentReason.DAMAGED]: 'Damaged',
  [StockAdjustmentReason.CORRECTION]: 'Correction',
  [StockAdjustmentReason.SHRINKAGE]: 'Shrinkage',
  [StockAdjustmentReason.SALE]: 'Sale',
  [StockAdjustmentReason.OTHER]: 'Other',
};

// Manual reasons only — `sale` is written by the sell transaction, never here.
const MANUAL_REASONS: StockAdjustmentReason[] = [
  StockAdjustmentReason.RECEIVED,
  StockAdjustmentReason.DAMAGED,
  StockAdjustmentReason.CORRECTION,
  StockAdjustmentReason.SHRINKAGE,
  StockAdjustmentReason.OTHER,
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function StockPage() {
  const propertyId = useSelectedPropertyId();
  const [adjusting, setAdjusting] = useState<StockRow | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useRetailStock({ propertyId, search: search.trim() || undefined });

  const allItems: StockRow[] = listQuery.data?.data ?? [];
  const items: StockRow[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (s) => s.name.toLowerCase().includes(q) || s.sku.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  return (
    <div>
      <PageHeader title="Stock" description="Inventory levels and adjustments" />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search stock"
        />
      </div>

      {!propertyId ? (
        <EmptyState icon={PackageSearch} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={PackageSearch}
          title="No products to stock"
          description="Add products to manage inventory levels."
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>SKU</Th>
                  <Th>On-hand</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((s) => (
                  <tr key={s.productId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{s.sku}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className="inline-flex items-center gap-1.5">
                        {s.stockOnHand <= 0 && (
                          <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
                        )}
                        {s.stockOnHand}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="secondary" onClick={() => setAdjusting(s)}>
                        <SlidersHorizontal size={14} aria-hidden="true" />
                        Adjust
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={adjusting !== null}
        onClose={() => setAdjusting(null)}
        title={adjusting ? `Adjust Inventory — ${adjusting.name}` : 'Adjust Inventory'}
        width="max-w-xl"
      >
        {adjusting && (
          <AdjustStockForm row={adjusting} onClose={() => setAdjusting(null)} />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function AdjustStockForm({ row, onClose }: { row: StockRow; onClose: () => void }) {
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState<StockAdjustmentReason>(StockAdjustmentReason.RECEIVED);
  const [note, setNote] = useState('');
  const [qtyError, setQtyError] = useState('');

  const adjustMutation = useAdjustStock();

  const qtyNum = parseInt(quantity, 10);
  const qtyValid = quantity.trim().length > 0 && Number.isInteger(qtyNum) && qtyNum >= 1;
  const delta = direction === 'add' ? qtyNum : -qtyNum;
  const projected = qtyValid ? row.stockOnHand + delta : row.stockOnHand;
  const wouldGoNegative = direction === 'remove' && qtyValid && projected < 0;
  const canApply = qtyValid && !wouldGoNegative;

  function clearError() {
    if (adjustMutation.isError) adjustMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!qtyValid) {
      setQtyError('Enter a whole quantity of 1 or more.');
      return;
    }
    if (wouldGoNegative) return;

    adjustMutation.mutate(
      {
        productId: row.productId,
        delta,
        reason,
        note: note.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
        <span className="text-gray-500">Current on-hand:</span>{' '}
        <span className="font-semibold text-gray-900">{row.stockOnHand}</span>
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">Direction</span>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => { setDirection('add'); clearError(); }}
            aria-pressed={direction === 'add'}
            className={cn(
              'px-4 py-2 text-sm font-medium',
              direction === 'add' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            Add (+)
          </button>
          <button
            type="button"
            onClick={() => { setDirection('remove'); clearError(); }}
            aria-pressed={direction === 'remove'}
            className={cn(
              'px-4 py-2 text-sm font-medium border-l border-gray-300',
              direction === 'remove' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            Remove (−)
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="adj-qty" className="block text-sm font-medium text-gray-700 mb-1">
          Quantity <span className="text-red-500">*</span>
        </label>
        <input
          id="adj-qty"
          type="number"
          min="1"
          step="1"
          required
          value={quantity}
          onChange={(e) => { setQuantity(e.target.value); setQtyError(''); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {qtyError && <p className="mt-1 text-xs text-red-600">{qtyError}</p>}
      </div>

      <div>
        <label htmlFor="adj-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="text-red-500">*</span>
        </label>
        <select id="adj-reason" value={reason} onChange={(e) => setReason(e.target.value as StockAdjustmentReason)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          {MANUAL_REASONS.map((r) => (
            <option key={r} value={r}>{REASON_LABELS[r]}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="adj-note" className="block text-sm font-medium text-gray-700 mb-1">Note</label>
        <input id="adj-note" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>

      <div
        className={cn(
          'rounded-lg border px-3 py-2 text-sm',
          wouldGoNegative ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-700',
        )}
        aria-live="polite"
      >
        New on-hand: <span className="font-semibold">{projected}</span>
        {wouldGoNegative && (
          <span className="block text-xs mt-0.5">Cannot remove more than on-hand ({row.stockOnHand}).</span>
        )}
      </div>

      {adjustMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(adjustMutation.error)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={adjustMutation.isPending} disabled={!canApply}>
          Apply Adjustment
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
